import { InternalLogicError } from '@/errors/InternalLogicError';
import { isSearchTreeLeafNode, SearchTreeNode } from '@/types';
import { sortPlaceholdersByPosition } from '@/util';
import { getPlaceholderFiltersInLeafNode } from '@/util/searchTree/placeholderFilters';
import { TemplatePlaceholderWithNodeAndFilterContext } from './types';

export const placeholderFieldHash = (leafNodeId: string, filterNameAndField: string) =>
  `${leafNodeId}:${filterNameAndField}`;

/**
 * Traverses all nodes in the tree, building an object containing of all
 * placeholders along with the id of the leaf node they were found on, and the
 * filterAndField name. The key of object is the hash of the leaf node id and
 * the filtername and field.
 *
 * It is worth noting the 4 different scenarios it can get called in. Firstly, 3
 * scenarios where placeholdersData state gets initialised inin
 * TemplatePanelContent:
 *
 * 1. When the user is loading an existing search template. In this case we want
 *    to make sure we pre-filled any existing placeholder data (e.g.
 *    descriptions)
 *
 * 2. When the user is building a normal search tree and clicks 'create
 *    template'. In this case the tree will not contain any placeholders yet,
 *    but we don't need to do anything different than in (1) - we'll simply end
 *    up with an empty object as intended.
 *
 * 3. When the user is creating a new search template from scratch. In this case
 *    initialSearchTree will be undefined, so we just want to return an empty
 *    object like in (2).
 *
 * And finally whenever the parent SearchTempatePage _imperatively_ calls the
 * syncSearchTree() method on the ref handle. In this case it calls
 * regegeneratePlaceholderData which only adds and removes placeholders, leaving
 * existing ones with the same values (but still creates a new object)
 *
 * TODO: Can we refactor the template page to use a a FlatSearchTree instead?
 */
export function findPlaceholdersInSearchTree(searchTree: SearchTreeNode | undefined) {
  return findPlaceholdersInSearchNode(searchTree);
}

/**
 * Returns placeholder hash keys in ascending order of placeholder.position.
 * Items without position sort to the end (stable). Used for initial
 * placeholderKeysInAddedOrder and for rendering order in PlaceholderFieldContentList.
 */
export function getPlaceholderKeysInPositionOrder(
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>
): string[] {
  const placeholdersWithKeys = Object.entries(placeholdersData).map(([key, data]) => ({
    ...data,
    recordKey: key,
  }));
  const sorted = sortPlaceholdersByPosition(placeholdersWithKeys);
  return sorted.map((item) => item.recordKey);
}

function findPlaceholdersInSearchNode(
  treeNode: SearchTreeNode | undefined
): Record<string, TemplatePlaceholderWithNodeAndFilterContext> {
  if (!treeNode) {
    return {};
  }

  if (isSearchTreeLeafNode(treeNode)) {
    const leafNodeId = treeNode._leafNodeId;
    if (!leafNodeId) {
      throw new InternalLogicError(
        '_leafNodeId not set on leaf node. Did not expect findPlaceholdersInSearchNode() to be called before these were populated.'
      );
    }
    const placeholdersInNode = getPlaceholderFiltersInLeafNode(treeNode);
    return Object.fromEntries(
      placeholdersInNode.map((x) => [
        placeholderFieldHash(leafNodeId, x.filterNameAndField),
        { ...x, leafNodeId },
      ])
    );
  } else {
    let nodesToReturn: Record<string, TemplatePlaceholderWithNodeAndFilterContext> = {};
    for (const node of treeNode.nodes) {
      nodesToReturn = {
        ...nodesToReturn,
        ...findPlaceholdersInSearchNode(node),
      };
    }
    return nodesToReturn;
  }
}
